// Typing profile: keys, ord og bigram-overgange på tværs af lektioner.

import type { LessonResult, RoundResult, UserProfile } from '../types';
import { getCumulativeUnlockedKeys, textUsesOnlyKeys } from './dictationUnits';

export const ERROR_DECAY = 0.9;
export const RT_EMA_ALPHA = 0.3;
export const MIN_KEY_SCORE = 2.5;
export const SLOW_MS_THRESHOLD = 650;
export const MIN_WORD_COUNT = 2;
export const MIN_BIGRAM_COUNT = 3;
export const BASELINE_MS_PER_CHAR = 120;

export interface WordStatAggregate {
  mistakes: number;
  totalMs: number;
  count: number;
}

export interface WeakSpotKeyRow {
  key: string;
  score: number;
  errors: number;
  avgMs: number | null;
  reasons: string[];
}

export interface WeakSpotBigramRow {
  bigram: string;
  label: string;
  score: number;
  errors: number;
  avgMs: number | null;
  count: number;
}

export interface WeakSpotWordRow {
  word: string;
  score: number;
  mistakes: number;
  avgMs: number;
  avgMsPerChar: number;
  count: number;
}

export interface WeakSpotsAnalysis {
  keys: WeakSpotKeyRow[];
  bigrams: WeakSpotBigramRow[];
  words: WeakSpotWordRow[];
  drillBigrams: string[];
  drillWords: string[];
  focusKeyLabel: string;
}

export function bigramKey(prev: string, next: string): string {
  return `${prev}|${next}`;
}

export function formatBigramLabel(key: string): string {
  const sep = key.indexOf('|');
  if (sep < 0) return key;
  const a = key.slice(0, sep);
  const b = key.slice(sep + 1);
  const fa = a === ' ' ? '␣' : a.toUpperCase();
  const fb = b === ' ' ? '␣' : b.toUpperCase();
  return `${fa} → ${fb}`;
}

export function formatKeyLabel(key: string): string {
  return key === ' ' ? 'mellemrum' : key.toUpperCase();
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

function decayRecord<T extends Record<string, number>>(
  src: T,
  decay: number,
  min: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(src)) {
    const d = v * decay;
    if (d >= min) out[k] = d;
  }
  return out;
}

function decayWordStats(
  src: Record<string, WordStatAggregate>,
): Record<string, WordStatAggregate> {
  const out: Record<string, WordStatAggregate> = {};
  for (const [w, s] of Object.entries(src)) {
    const mistakes = s.mistakes * ERROR_DECAY;
    const count = s.count * ERROR_DECAY;
    if (mistakes < 0.25 && count < 0.5) continue;
    out[w] = {
      mistakes,
      totalMs: s.totalMs * ERROR_DECAY,
      count,
    };
  }
  return out;
}

function mergeWordStatEntry(
  target: Record<string, WordStatAggregate>,
  word: string,
  add: WordStatAggregate,
) {
  if (!word || word.length < 2) return;
  const w = word.toLowerCase();
  if (!target[w]) {
    target[w] = { mistakes: 0, totalMs: 0, count: 0 };
  }
  target[w].mistakes += add.mistakes;
  target[w].totalMs += add.totalMs;
  target[w].count += add.count;
}

export function mergeWordStatsFromRound(
  target: Record<string, WordStatAggregate>,
  round: RoundResult,
) {
  if (!round.wordStats) return;
  for (const [word, s] of Object.entries(round.wordStats)) {
    mergeWordStatEntry(target, word, s);
  }
}

export function mergeTypingProfileFromLesson(
  user: UserProfile,
  result: LessonResult,
): Pick<
  UserProfile,
  'errorsByKey' | 'responseTimeAvgs' | 'wordStats' | 'errorsByBigram' | 'transitionTimeAvgs'
> {
  const errorsByKey: Record<string, number> = decayRecord(
    user.errorsByKey,
    ERROR_DECAY,
    0.5,
  );
  for (const [k, v] of Object.entries(result.errorsByKey)) {
    errorsByKey[k] = (errorsByKey[k] ?? 0) + v;
  }

  const responseTimeAvgs: Record<string, number> = {
    ...user.responseTimeAvgs,
  };
  for (const [key, samples] of Object.entries(result.responseTimes)) {
    if (samples.length === 0) continue;
    const lessonAvg = median(samples);
    const prev = responseTimeAvgs[key];
    responseTimeAvgs[key] =
      prev === undefined
        ? lessonAvg
        : prev * (1 - RT_EMA_ALPHA) + lessonAvg * RT_EMA_ALPHA;
  }

  const wordStats = decayWordStats(user.wordStats ?? {});
  for (const round of result.rounds) {
    mergeWordStatsFromRound(wordStats, round);
  }

  const errorsByBigram: Record<string, number> = decayRecord(
    user.errorsByBigram ?? {},
    ERROR_DECAY,
    0.5,
  );
  const transitionTimeAvgs: Record<string, number> = {
    ...(user.transitionTimeAvgs ?? {}),
  };

  for (const round of result.rounds) {
    for (const [bg, v] of Object.entries(round.errorsByBigram ?? {})) {
      errorsByBigram[bg] = (errorsByBigram[bg] ?? 0) + v;
    }
    for (const [bg, samples] of Object.entries(round.transitionTimes ?? {})) {
      if (samples.length === 0) continue;
      const lessonAvg = median(samples);
      const prev = transitionTimeAvgs[bg];
      transitionTimeAvgs[bg] =
        prev === undefined
          ? lessonAvg
          : prev * (1 - RT_EMA_ALPHA) + lessonAvg * RT_EMA_ALPHA;
    }
  }

  return {
    errorsByKey,
    responseTimeAvgs,
    wordStats,
    errorsByBigram,
    transitionTimeAvgs,
  };
}

/** Backfill word/bigram stats from saved recentResults for existing users. */
export function backfillTypingProfile(user: UserProfile): UserProfile {
  const hasWords = Object.keys(user.wordStats ?? {}).length > 0;
  const hasBigrams = Object.keys(user.errorsByBigram ?? {}).length > 0;
  if (hasWords && hasBigrams) return user;

  let wordStats = { ...(user.wordStats ?? {}) };
  let errorsByBigram = { ...(user.errorsByBigram ?? {}) };
  let transitionTimeAvgs = { ...(user.transitionTimeAvgs ?? {}) };

  for (const lesson of user.recentResults) {
    for (const round of lesson.rounds) {
      if (!hasWords) mergeWordStatsFromRound(wordStats, round);
      for (const [bg, v] of Object.entries(round.errorsByBigram ?? {})) {
        errorsByBigram[bg] = (errorsByBigram[bg] ?? 0) + v;
      }
      for (const [bg, samples] of Object.entries(round.transitionTimes ?? {})) {
        if (samples.length === 0) continue;
        const avg = median(samples);
        const prev = transitionTimeAvgs[bg];
        transitionTimeAvgs[bg] =
          prev === undefined ? avg : (prev + avg) / 2;
      }
    }
  }

  return {
    ...user,
    wordStats: hasWords ? user.wordStats : wordStats,
    errorsByBigram: hasBigrams ? user.errorsByBigram : errorsByBigram,
    transitionTimeAvgs: user.transitionTimeAvgs ?? transitionTimeAvgs,
  };
}

export function rankWeakSpotKeys(
  user: UserProfile,
  allowedKeys: ReadonlyArray<string>,
  limit = 6,
): WeakSpotKeyRow[] {
  const allowed = new Set(allowedKeys);
  const rows = new Map<string, WeakSpotKeyRow>();

  for (const [key, count] of Object.entries(user.errorsByKey)) {
    if (!allowed.has(key)) continue;
    const errors = count;
    rows.set(key, {
      key,
      score: errors * 12,
      errors,
      avgMs: user.responseTimeAvgs[key] ?? null,
      reasons: errors >= 1 ? ['fejl'] : [],
    });
  }

  for (const [key, avgMs] of Object.entries(user.responseTimeAvgs)) {
    if (!allowed.has(key)) continue;
    const slowBonus = Math.max(0, avgMs - SLOW_MS_THRESHOLD) / 80;
    if (slowBonus <= 0) continue;
    const existing = rows.get(key);
    if (existing) {
      existing.score += slowBonus;
      existing.avgMs = avgMs;
      if (!existing.reasons.includes('tøven')) existing.reasons.push('tøven');
    } else {
      rows.set(key, {
        key,
        score: slowBonus,
        errors: 0,
        avgMs,
        reasons: ['tøven'],
      });
    }
  }

  return [...rows.values()]
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function rankWeakSpotBigrams(
  user: UserProfile,
  allowedKeys: ReadonlyArray<string>,
  limit = 6,
): WeakSpotBigramRow[] {
  const allowed = new Set(allowedKeys);
  const rows: WeakSpotBigramRow[] = [];
  const keys = new Set([
    ...Object.keys(user.errorsByBigram ?? {}),
    ...Object.keys(user.transitionTimeAvgs ?? {}),
  ]);

  for (const bg of keys) {
    const sep = bg.indexOf('|');
    if (sep < 0) continue;
    const prev = bg.slice(0, sep);
    const next = bg.slice(sep + 1);
    if (!allowed.has(prev) || !allowed.has(next)) continue;

    const errors = user.errorsByBigram?.[bg] ?? 0;
    const avgMs = user.transitionTimeAvgs?.[bg];
    const slowBonus =
      avgMs !== undefined ? Math.max(0, avgMs - 400) / 60 : 0;
    const score = errors * 12 + slowBonus;
    if (score < MIN_KEY_SCORE) continue;

    rows.push({
      bigram: bg,
      label: formatBigramLabel(bg),
      score,
      errors,
      avgMs: avgMs ?? null,
      count: Math.max(errors, avgMs !== undefined ? MIN_BIGRAM_COUNT : 0),
    });
  }

  return rows
    .filter((r) => r.errors >= 0.5 || (r.avgMs ?? 0) > 400)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function rankWeakSpotWords(
  user: UserProfile,
  allowedKeys: ReadonlyArray<string>,
  limit = 8,
): WeakSpotWordRow[] {
  const rows: WeakSpotWordRow[] = [];

  for (const [word, s] of Object.entries(user.wordStats ?? {})) {
    if (word.length < 2 || s.count < MIN_WORD_COUNT) continue;
    if (!textUsesOnlyKeys(word, allowedKeys)) continue;
    const avgMs = s.totalMs / s.count;
    const avgMsPerChar = avgMs / word.length;
    const slowPenalty = Math.max(0, avgMsPerChar - BASELINE_MS_PER_CHAR) / 50;
    const score = s.mistakes * 10 + slowPenalty;
    if (score < MIN_KEY_SCORE) continue;

    rows.push({
      word,
      score,
      mistakes: s.mistakes,
      avgMs,
      avgMsPerChar,
      count: s.count,
    });
  }

  return rows.sort((a, b) => b.score - a.score).slice(0, limit);
}

export function analyzeWeakSpots(user: UserProfile): WeakSpotsAnalysis {
  const allowed = getCumulativeUnlockedKeys(user);
  const keys = rankWeakSpotKeys(user, allowed);
  const bigrams = rankWeakSpotBigrams(user, allowed);
  const words = rankWeakSpotWords(user, allowed);

  const drillBigrams = bigrams
    .map((b) => b.bigram.replace('|', ''))
    .filter((s) => s.length >= 2 && textUsesOnlyKeys(s, allowed))
    .slice(0, 6);

  const drillWords = words
    .map((w) => w.word)
    .filter((w) => textUsesOnlyKeys(w, allowed))
    .slice(0, 8);

  const focusKeyLabel = keys
    .slice(0, 4)
    .map((k) => formatKeyLabel(k.key))
    .join(' · ');

  return {
    keys,
    bigrams,
    words,
    drillBigrams,
    drillWords,
    focusKeyLabel,
  };
}

export function isWeakSpotsEligible(user: UserProfile): boolean {
  if (!user.completedTierIds.includes('grundniveau')) return false;
  const analysis = analyzeWeakSpots(user);
  return (
    analysis.keys.some((k) => k.score >= MIN_KEY_SCORE) ||
    analysis.bigrams.length > 0 ||
    analysis.words.length > 0
  );
}

export function formatWeakSpotKeysLabel(user: UserProfile): string {
  return analyzeWeakSpots(user).focusKeyLabel;
}
